import {
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signOut,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    sendPasswordResetEmail,
    fetchSignInMethodsForEmail,
    updatePassword,
    sendEmailVerification,
} from 'firebase/auth';
import type { User, IdTokenResult } from 'firebase/auth';
import { auth } from '../config/firebase';
import { HTTP_API_URL } from '../config/api';
import type { DeleteMode } from '../types/markers';

export interface AuthUser {
    uid: string;
    email: string | null;
    displayName: string | null;
    emailVerified: boolean;
    getIdToken: () => Promise<string>;
}

export interface UserProfile {
    profile: {
        email: string;
        displayName?: string;
        monthlyDownloadUsage?: number;
        storageUsageBytes?: number;
        [key: string]: unknown;
    };
    credits: {
        balance: number;
        total_earned: number;
        total_spent: number;
    };
    subscription?: Record<string, unknown>; // Definire meglio se necessario
}

export interface MapMarkerResponse {
    batch_id?: string;
    batch_name?: string;
    photo_id?: string;
    lat?: number;
    lng?: number;
    filename?: string;
    original_filename?: string;
    photo_path?: string;
    image_url?: string;
    thumbnail?: string;
    thumbnail_url?: string;
    thumbnail_path?: string;
    content_hash?: string;
    processed_at?: string;
    status?: string;
    output_relative_path?: string;
    final_location?: unknown;
    final_date?: unknown;
    country_code?: string;
    admin_area?: string;
    locality?: string;
    neighborhood?: string;
    postcode?: string;
    marker_hidden?: boolean;  // Flag indicating if marker was hidden via "Remove marker only"
    unmarked?: boolean;  // Flag indicating if image has no GPS coordinates
    vector_distance?: number;
    has_final_date?: boolean;
    final_date_ts?: string;
}

export interface DuplicateGroupResponse {
    content_hash: string;
    group_size: number;
    markers: MapMarkerResponse[];
}

export interface DeleteMarkerPayload {
    batch_id: string;
    photo_id: string;
    mode?: DeleteMode;
}

export interface DeleteDuplicatesPayload {
    content_hash: string;
    keep: {
        batch_id: string;
        photo_id: string;
    };
    mode?: DeleteMode;
}

export interface PhotoSearchFilters {
    date_from?: string;
    date_to?: string;
    country_code?: string;
    locality?: string;
    neighborhood?: string;
    include_unknown_dates?: boolean;
    batch_id?: string;
}

export interface PhotoSearchResponse {
    results: MapMarkerResponse[];
    count?: number;
    next_offset?: number | null;
    next_page_token?: string | null;
    mode?: string;
}

class AuthService {
    private static instance: AuthService;

    public static getInstance(): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService();
        }
        return AuthService.instance;
    }

    /**
     * Send a verification email to the currently signed-in user.
     */
    async sendEmailVerification(): Promise<void> {
        const currentUser = this.getCurrentUser();
        if (!currentUser) {
            throw new Error('User not authenticated');
        }
        await sendEmailVerification(currentUser, {
            url: `${window.location.origin}/auth/complete`,
        });
    }

    /**
     * Request server-side approval to send a verification email (cooldown/daily limits).
     */
    async requestVerificationEmail(): Promise<{ cooldown_seconds?: number; remaining_today?: number }> {
        const token = await this.requireAuthToken({ allowUnverified: true });

        const response = await fetch(`${HTTP_API_URL}/api/auth/send-verification`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const detail = await response.json().catch(() => null);
            const error: any = new Error(
                detail?.detail?.message ||
                detail?.detail ||
                response.statusText ||
                'Failed to request verification email'
            );
            const retryAfter =
                detail?.detail?.retry_after_seconds ??
                detail?.retry_after_seconds;
            if (retryAfter !== undefined) {
                error.retryAfterSeconds = retryAfter;
            }
            throw error;
        }

        return await response.json();
    }

    /**
     * Invia link di accesso via email
     */
    async sendSignInLinkToEmail(email: string): Promise<boolean> {
        try {
            console.log('📧 AuthService: Sending sign-in link to:', email);

            const actionCodeSettings = {
                // URL di redirect dopo accesso (deve essere nella whitelist)
                url: `${window.location.origin}/auth/complete`,
                // Dinamico per gestire meglio i link
                handleCodeInApp: true,
            };

            console.log('🔗 ActionCodeSettings:', actionCodeSettings);

            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            console.log('✅ AuthService: Link sent successfully');

            // Salva email nel localStorage per completare l'accesso
            localStorage.setItem('emailForSignIn', email);

            return true;
        } catch (error) {
            console.error('❌ AuthService: Error sending email:', error);
            throw error;
        }
    }

    /**
     * Verifica se l'URL corrente è un link di accesso
     */
    isSignInWithEmailLink(url: string): boolean {
        return isSignInWithEmailLink(auth, url);
    }

    /**
     * Completa l'accesso con email link
     */
    async signInWithEmailLink(email: string, emailLink: string): Promise<User> {
        try {
            console.log('🔐 AuthService: Attempting sign-in with email link');
            console.log('📧 Email:', email);
            console.log('🔗 Email link:', emailLink);

            const result = await signInWithEmailLink(auth, email, emailLink);
            console.log('✅ AuthService: Sign-in completed, user:', result.user.email);

            // Pulisci localStorage
            localStorage.removeItem('emailForSignIn');

            return result.user;
        } catch (error) {
            console.error('❌ AuthService: Error signing in with email link:', error);

            const firebaseError = error as { code?: string; message?: string };
            // Analisi dettagliata dell'errore Firebase
            if (firebaseError.code) {
                console.error('🔍 Firebase error code:', firebaseError.code);
                console.error('📝 Firebase error message:', firebaseError.message);

                switch (firebaseError.code) {
                    case 'auth/invalid-action-code':
                        console.error('💡 Possible causes:');
                        console.error('   - Email link expired (after 1 hour)');
                        console.error('   - Link already used');
                        console.error('   - Firebase configuration issue');
                        console.error('   - Domain not authorized');
                        break;
                    case 'auth/expired-action-code':
                        console.error('💡 Email link expired');
                        break;
                    case 'auth/user-disabled':
                        console.error('💡 User account disabled');
                        break;
                    default:
                        console.error('💡 Unknown Firebase error');
                }
            }

            throw error;
        }
    }

    /**
     * Logout utente
     */
    async signOut(): Promise<void> {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    }

    /**
     * Listener stato autenticazione
     */
    onAuthStateChange(callback: (user: User | null) => void): () => void {
        return onAuthStateChanged(auth, callback);
    }

    /**
     * Verifica se utente è autenticato
     */
    isAuthenticated(user: User | null): boolean {
        return user !== null;
    }

    /**
     * Ottieni utente corrente
     */
    getCurrentUser(): User | null {
        return auth.currentUser;
    }

    public async requireAuthToken(options?: { forceRefresh?: boolean; allowUnverified?: boolean }): Promise<string> {
        const currentUser = this.getCurrentUser();
        if (!currentUser) {
            throw new Error('User not authenticated');
        }
        let shouldForceRefresh = options?.forceRefresh === true;
        let forceFlag = false;

        try {
            forceFlag = !!localStorage.getItem('forceTokenRefresh');
            if (forceFlag) {
                shouldForceRefresh = true;
            }
        } catch (err) {
            console.warn('Unable to read forceTokenRefresh flag:', err);
        }

        // Se il token corrente riporta email non verificata, prova a ricaricare
        if (!currentUser.emailVerified) {
            try {
                await currentUser.reload();
                if (currentUser.emailVerified) {
                    shouldForceRefresh = true;
                }
            } catch (err) {
                console.warn('Unable to reload user before token fetch:', err);
            }
        }

        const clearForceFlag = () => {
            if (forceFlag) {
                try {
                    localStorage.removeItem('forceTokenRefresh');
                } catch (err) {
                    console.warn('Unable to clear forceTokenRefresh flag:', err);
                }
            }
        };

        let tokenResult: IdTokenResult;
        try {
            tokenResult = await currentUser.getIdTokenResult(shouldForceRefresh);
            clearForceFlag();
        } catch (err) {
            console.warn('Unable to fetch ID token (will retry with force):', err);
            tokenResult = await currentUser.getIdTokenResult(true);
            clearForceFlag();
        }

        const claimsEmailVerified = (tokenResult?.claims as any)?.email_verified;
        if (claimsEmailVerified === false && currentUser.emailVerified) {
            try {
                await currentUser.reload();
            } catch (err) {
                console.warn('Reload before forcing token refresh failed:', err);
            }
            tokenResult = await currentUser.getIdTokenResult(true);
        }

        if (!options?.allowUnverified && (currentUser.emailVerified === false || claimsEmailVerified === false)) {
            const err: any = new Error('Email not verified. Please verify your email to continue.');
            err.code = 'email-not-verified';
            throw err;
        }

        return tokenResult.token;
    }

    /**
     * Registrazione con email e password
     */
    async signUpWithEmailAndPassword(email: string, password: string): Promise<User> {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            return result.user;
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    }

    /**
     * Login con email e password
     */
    async signInWithEmailAndPassword(email: string, password: string): Promise<User> {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            return result.user;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    /**
     * Login con Google
     */
    async signInWithGoogle(): Promise<User> {
        try {
            const provider = new GoogleAuthProvider();
            // Aggiungi scope per profilo e email
            provider.addScope('email');
            provider.addScope('profile');

            const result = await signInWithPopup(auth, provider);
            return result.user;
        } catch (error) {
            console.error('Google Sign-In error:', error);
            throw error;
        }
    }

    /**
     * Invia email di reset password
     */
    async sendPasswordResetEmail(email: string): Promise<boolean> {
        try {
            await sendPasswordResetEmail(auth, email);
            return true;
        } catch (error) {
            console.error('Error sending password reset:', error);
            throw error;
        }
    }

    /**
     * List the sign-in methods available for an email (e.g. password, emailLink, google.com).
     */
    async fetchSignInMethods(email: string): Promise<string[]> {
        try {
            return await fetchSignInMethodsForEmail(auth, email);
        } catch (error) {
            console.error('Error fetching sign-in methods:', error);
            throw error;
        }
    }

    /**
     * Set or update the password for the currently signed-in user.
     * Requires a recent sign-in (e.g. just completed magic link).
     */
    async setPasswordForCurrentUser(password: string): Promise<void> {
        const currentUser = this.getCurrentUser();
        if (!currentUser) {
            throw new Error('User not authenticated');
        }
        try {
            await updatePassword(currentUser, password);
        } catch (error) {
            console.error('Error updating password:', error);
            throw error;
        }
    }

    /**
     * Check what authentication providers are associated with an email.
     * Returns { exists: boolean, providers: string[] }
     */
    async checkUserProviders(email: string): Promise<{ exists: boolean; providers: string[] }> {
        try {
            const response = await fetch(`${HTTP_API_URL}/api/auth/check-user-providers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                throw new Error('Failed to check user providers');
            }

            return await response.json();
        } catch (error) {
            console.error('Error checking user providers:', error);
            throw error;
        }
    }

    /**
     * Open Google popup and return the credential + email without signing in.
     * This allows checking if we should link accounts first.
     */
    async getGoogleCredential(): Promise<{ credential: any; email: string; user: any }> {
        try {
            const provider = new GoogleAuthProvider();
            provider.addScope('email');
            provider.addScope('profile');

            // Use signInWithPopup but we'll handle the result specially
            const result = await signInWithPopup(auth, provider);
            const credential = GoogleAuthProvider.credentialFromResult(result);

            // Sign out immediately - we just wanted the credential
            await signOut(auth);

            return {
                credential,
                email: result.user.email || '',
                user: result.user
            };
        } catch (error) {
            console.error('Error getting Google credentials:', error);
            throw error;
        }
    }

    /**
     * Sign in using an existing Google credential (no popup needed).
     */
    async signInWithGoogleCredential(credential: any): Promise<User> {
        try {
            const { signInWithCredential } = await import('firebase/auth');
            const result = await signInWithCredential(auth, credential);
            return result.user;
        } catch (error) {
            console.error('Error signing in with Google credential:', error);
            throw error;
        }
    }

    /**
     * Sign in with email/password, then link Google credential to the account.
     * This preserves the password while adding Google as a provider.
     */
    async linkGoogleToAccount(email: string, password: string, googleCredential: any): Promise<User> {
        try {
            // First sign in with email/password
            const result = await signInWithEmailAndPassword(auth, email, password);

            if (!result.user.emailVerified) {
                const err: any = new Error('Email not verified. Verify your email (or use the magic link) before linking with Google.');
                err.code = 'email-not-verified';
                await signOut(auth).catch(() => undefined);
                throw err;
            }

            // Then link the Google credential
            const { linkWithCredential } = await import('firebase/auth');
            await linkWithCredential(result.user, googleCredential);

            return result.user;
        } catch (error) {
            console.error('Error linking Google account:', error);
            throw error;
        }
    }

    /**
     * Verifica se un utente esiste controllando tramite il backend (Admin SDK).
     * Restituisce true se l'utente esiste, false altrimenti.
     * Bypassa la protezione enumerazione email lato client.
     */
    async checkUserExists(email: string): Promise<boolean> {
        try {
            const response = await fetch(`${HTTP_API_URL}/api/auth/check-user-exists`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                console.error('Error checking user existence:', response.statusText);
                // On error, we can't determine existence, throw to handle in component
                throw new Error('Failed to check user existence');
            }

            const data = await response.json();
            return data.exists === true;
        } catch (error) {
            console.error('Error checking user existence:', error);
            throw error;
        }
    }

    /**
     * Recupera il profilo utente dal backend
     */
    async fetchUserProfile(token: string): Promise<UserProfile> {
        try {
            const response = await fetch(`${HTTP_API_URL}/api/auth/profile`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch user profile');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching user profile:', error);
            throw error;
        }
    }

    /**
     * Recupera i dati di pricing dal backend
     */
    async getPricingData(): Promise<unknown> {
        try {
            const token = await this.requireAuthToken();

            const response = await fetch(`${HTTP_API_URL}/api/auth/pricing`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch pricing data');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching pricing data:', error);
            throw error;
        }
    }

    /**
     * Recupera l'ultimo errore di pagamento (se presente) e lo azzera lato backend.
     */
    async getPaymentStatus(): Promise<{ latest_failure?: { message?: string } } | null> {
        try {
            const currentUser = this.getCurrentUser();
            if (!currentUser) {
                return null;
            }
            const token = await this.requireAuthToken();

            const response = await fetch(`${HTTP_API_URL}/api/auth/payment-status`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch payment status');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching payment status:', error);
            return null;
        }
    }

    /**
     * Crea una sessione di checkout con supporto valuta
     */
    async createCheckoutSession(productId: string, currency?: string): Promise<string> {
        try {
            const token = await this.requireAuthToken();

            const requestBody: { product_id: string; currency?: string } = { product_id: productId };
            if (currency) {
                requestBody.currency = currency;
            }

            const response = await fetch(`${HTTP_API_URL}/api/auth/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error('Failed to create checkout session');
            }

            const data = await response.json();
            return data.checkout_url;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            throw error;
        }
    }

    async fetchMapMarkers(): Promise<MapMarkerResponse[]> {
        try {
            const token = await this.requireAuthToken();
            const response = await fetch(`${HTTP_API_URL}/api/map/markers`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.status === 403) {
                return [];
            }

            if (!response.ok) {
                throw new Error('Failed to fetch map markers');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching map markers:', error);
            throw error;
        }
    }

    async fetchDuplicateGroups(): Promise<DuplicateGroupResponse[]> {
        try {
            const token = await this.requireAuthToken();
            const response = await fetch(`${HTTP_API_URL}/api/map/duplicates`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.status === 403) {
                return [];
            }

            if (!response.ok) {
                throw new Error('Failed to fetch duplicate groups');
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching duplicate groups:', error);
            throw error;
        }
    }

    async deleteMarker(payload: DeleteMarkerPayload): Promise<void> {
        try {
            const token = await this.requireAuthToken();
            const response = await fetch(`${HTTP_API_URL}/api/map/delete-marker`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    batch_id: payload.batch_id,
                    photo_id: payload.photo_id,
                    mode: payload.mode ?? 'marker_only',
                }),
            });

            if (!response.ok) {
                const detail = await response.json().catch(() => null);
                throw new Error(detail?.detail || 'Failed to delete marker.');
            }
        } catch (error) {
            console.error('Error deleting marker:', error);
            throw error;
        }
    }

    async deleteDuplicateGroup(payload: DeleteDuplicatesPayload): Promise<void> {
        try {
            const token = await this.requireAuthToken();
            const response = await fetch(`${HTTP_API_URL}/api/map/delete-duplicates`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    content_hash: payload.content_hash,
                    keep: payload.keep,
                    mode: payload.mode ?? 'marker_only',
                }),
            });

            if (!response.ok) {
                const detail = await response.json().catch(() => null);
                throw new Error(detail?.detail || 'Failed to delete duplicate group.');
            }
        } catch (error) {
            console.error('Error deleting duplicates:', error);
            throw error;
        }
    }

    async searchPhotos(payload: {
        query_text: string;
        filters?: PhotoSearchFilters;
        limit?: number;
        offset?: number;
        page_token?: string | null;
    }): Promise<PhotoSearchResponse> {
        const token = await this.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/api/search/photos`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                query_text: payload.query_text,
                filters: payload.filters,
                limit: payload.limit,
                offset: payload.offset,
                page_token: payload.page_token,
            }),
        });

        if (response.status === 503) {
            return { results: [] };
        }

        if (!response.ok) {
            const detail = await response.json().catch(() => null);
            throw new Error(detail?.detail || 'Failed to run semantic search.');
        }

        return await response.json();
    }

    async searchPhotosByImage(payload: {
        batch_id: string;
        photo_id: string;
        limit?: number;
        page_token?: string | null;
    }): Promise<PhotoSearchResponse> {
        const token = await this.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/api/search/photos/by-image`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                batch_id: payload.batch_id,
                photo_id: payload.photo_id,
                limit: payload.limit,
                page_token: payload.page_token,
            }),
        });

        if (response.status === 503) {
            return { results: [] };
        }

        if (!response.ok) {
            const detail = await response.json().catch(() => null);
            throw new Error(detail?.detail || 'Failed to run similar-image search.');
        }

        return await response.json();
    }

    async deleteAllStorage(): Promise<any> {
        const token = await this.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/api/storage/delete-all`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
        });

        if (!response.ok) {
            const detail = await response.text();
            throw new Error(detail || 'Failed to delete all storage');
        }

        return response.json();
    }

    async deleteBatchStorage(batchId: string): Promise<any> {
        const token = await this.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/api/storage/delete-batch`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ batch_id: batchId }),
        });

        if (!response.ok) {
            const detail = await response.text();
            throw new Error(detail || 'Failed to delete batch storage');
        }

        return response.json();
    }

    async getSignedUrlForBlob(blobPath: string): Promise<string> {
        try {
            const token = await this.requireAuthToken();
            const response = await fetch(`${HTTP_API_URL}/prompt/image-url`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ blob_path: blobPath }),
            });

            if (!response.ok) {
                const detail = await response.json().catch(() => null);
                throw new Error(detail?.detail || 'Failed to generate signed URL.');
            }

            const data = await response.json();
            return data.url;
        } catch (error) {
            console.error('Error generating signed URL:', error);
            throw error;
        }
    }

    /**
     * Verifica lo stato di un batch nel backend
     * @param batchId ID del batch da verificare
     * @returns Promise<BatchStatus> Stato del batch
     */
    async checkBatchStatus(batchId: string): Promise<{
        status: 'deleted' | 'completed' | 'processing' | 'waiting_start' | 'unknown';
        batch_name?: string;
        batch_created_at?: string;
        batch_completed_at?: string;
        total_files?: number;
        processed_files?: number;
        message?: string;
        restorable?: boolean;
        uploads_present?: boolean;
        uploads_count?: number;
        uploads_total_bytes?: number;
    }> {
        try {
            if (!batchId) {
                console.warn('⚠️ checkBatchStatus: batchId missing');
                return { status: 'unknown', message: 'Batch ID not provided' };
            }

            const token = await this.requireAuthToken();
            console.log('🔍 Checking batch status:', batchId);

            const response = await fetch(`${HTTP_API_URL}/api/batch/status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ batch_id: batchId }),
            });

            if (!response.ok) {
                const detail = await response.json().catch(() => null);
                console.warn('⚠️ Error checking batch status:', detail?.detail || response.statusText);
                return {
                    status: 'unknown',
                    message: detail?.detail || `HTTP error ${response.status}`
                };
            }

            const result = await response.json();
            console.log('✅ Batch status received:', result);

            return result;
        } catch (error) {
            console.error('❌ Error checking batch status:', error);
            return {
                status: 'unknown',
                message: `Network error: ${error instanceof Error ? error.message : 'Unknown'}`
            };
        }
    }

    /**
     * Recupera lo stato job pendente dell'utente (pending_upload_batch).
     * Usato quando il client non ha mai ricevuto il batch_id (rete interrotta
     * durante l'init del signed upload, refresh, altro dispositivo/browser)
     * per riattivare la possibilità di usare "Clear uploads".
     */
    async getPendingJobState(): Promise<{
        batch_id?: string | null;
        pending_upload_batch?: Record<string, unknown>;
        active_jobs?: {
            processing_batches?: Record<string, unknown>;
            compression_jobs?: Record<string, unknown>;
        };
    }> {
        const token = await this.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/api/job-state/pending`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: '{}',
        });

        if (!response.ok) {
            const detail = await response.text().catch(() => null);
            throw new Error(detail || `HTTP error ${response.status}`);
        }

        return response.json();
    }

    async recoverStaleBatch(
        batchId: string,
        options?: { force?: boolean; source?: string },
    ): Promise<{
        requeued: boolean;
        reason?: string;
        candidate_step?: string;
        candidate_file?: string;
    }> {
        const token = await this.requireAuthToken();
        const response = await fetch(`${HTTP_API_URL}/api/batch/recover-stale`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                batch_id: batchId,
                force: options?.force === true,
                source: options?.source ?? 'restore_batch',
            }),
        });

        if (!response.ok) {
            const detail = await response.json().catch(() => null);
            throw new Error(detail?.detail || 'Failed to recover stale batch.');
        }

        return await response.json();
    }
}

export const authService = AuthService.getInstance();
